import React from 'react';
import { Link } from 'react-router-dom';
import reactStringReplace from 'react-string-replace';

export const kFormatter = num => {
  if (num > 999999) return `${(num / 1000000).toString().match(/^\d+(?:\.\d{0,1})?/)}M`; // no rounding
  if (num > 999) return `${(num / 1000).toFixed(1)}K`;

  return num;
};

/**
 *  [arrayToCommaList] - take array of data and return comma separated list.
 *  NOTE: Checkout CommaList component if you are looking to return a react component
 *  WARNING: Should only be using for tests or cases where returning text without html is desired
 *
 *  @param {object[] | string[] | null} data
 *
 *  @param {object} options
 *  @param {string} [options.attribute] - if data format is an object, attribute defines the object key to use
 *  to form string with
 *  @param {number} [options.limit]
 *  @param {boolean} [options.ampersand] - will add an ampersand before the last item in array instead of a comma
 *
 *  @returns {string}
 */

// data should be array of strings unless passing urlAttribute, then returns html with links
export const arrayToCommaList = (data, options = {}) => {
  const { attribute, limit, ampersand } = options;
  if (!data) return null;

  return data.reduce((accum, element, i) => {
    if (limit && i === limit) return `${accum}, ...`;
    if (limit && i >= limit) return accum;

    const string = attribute ? element[attribute] : element;
    const separator = ampersand && i + 1 === data.length ? ' & ' : ', ';
    const isComma = i > 0 ? separator : '';

    // fail safe just in case string is undefined
    if (!string) return accum;

    return `${accum}${isComma}${string}`;
  }, '');
};

export const formatSecondsDuration = seconds => {
  const d = Math.floor(seconds);
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);
  return `${(h > 0 ? `${h}:${m < 10 ? '0' : ''}` : '') + m}:${s < 10 ? '0' : ''}${s}`;
};

/**
 * [getActiveYears] - takes start/end years of artists active years and returns it formatted correctly
 *  @param {string} startYear
 *  @param {string} endYear
 */

export const getActiveYears = (startYear, endYear) => {
  const currentYear = new Date().getFullYear();

  if (!startYear && !endYear) return null;
  if (isNaN(startYear) || isNaN(endYear)) return null;
  if (startYear === currentYear) return startYear;
  if (endYear !== currentYear) return `${startYear} - ${endYear}`;
  return `${startYear} - Present`;
};

export const addMatchingArtistLinksToString = (text, artists) => {
  if (!artists.length) return text;

  return artists.reduce((accum, artist) => {
    return reactStringReplace(accum.length ? accum : text, artist.name, match => (
      <Link to={artist.slug} key={artist.id}>
        {match}
      </Link>
    ));
  }, []);
};

// Referenced MIT License package
// https://github.com/sindresorhus/copy-text-to-clipboard/blob/main/index.js

export const copyTextToClipboard = (input, { target = document.body } = {}) => {
  const element = document.createElement('textarea');
  const previouslyFocusedElement = document.activeElement;

  element.value = input;

  // Prevent keyboard from showing on mobile
  element.setAttribute('readonly', '');

  element.style.contain = 'strict';
  element.style.position = 'absolute';
  element.style.left = '-9999px';
  element.style.fontSize = '12pt'; // Prevent zooming on iOS

  const selection = document.getSelection();
  const originalRange = selection.rangeCount > 0 && selection.getRangeAt(0);

  target.append(element);
  element.select();

  // Explicit selection workaround for iOS
  element.selectionStart = 0;
  element.selectionEnd = input.length;

  let isSuccess = false;
  try {
    isSuccess = document.execCommand('copy');
  } catch {}

  element.remove();

  if (originalRange) {
    selection.removeAllRanges();
    selection.addRange(originalRange);
  }

  // Get the focus back on the previously focused element, if any
  if (previouslyFocusedElement) {
    previouslyFocusedElement.focus();
  }

  return isSuccess;
};
